import { api, type ApiResponse } from './client';

export interface TaskTag {
  id: number;
  tag_name: string;
  color: string;
}

export interface Task {
  task_id: string;
  url: string;
  domain: string;
  target_keyword?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  project_id?: string;
  priority?: string;
  retry_count?: number;
  created_at: string;
  completed_at?: string;
  duration_ms?: number;
  archived: boolean;
  success_strategy?: string;
  success_phase?: number;
  serp_request_id?: string | null;
  tags?: TaskTag[];
}

export interface TaskStats {
  total: number;
  completed: number;
  pending: number;
  processing: number;
  failed: number;
  avg_duration_ms?: number;
}

export interface DomainStats {
  domain: string;
  total_tasks: number;
  completed: number;
  failed: number;
  processing: number;
  pending: number;
  avg_duration_ms: number;
  best_strategy: string | null;
  best_phase: number | null;
  success_rate: number;
  first_seen: string;
  last_scraped: string | null;
  is_blocklisted: boolean;
  paused_until: string | null;
  pause_reason: string | null;
  strategies: Record<string, { success: number; fail: number; avg_ms: number }>;
}

export interface AlibabaModules {
  attributes: Array<{ key: string; value: string }>;
  price_tiers: Array<{ quantity: string; price: string }>;
  variations: Array<{ name: string; options: string[] }>;
  lead_time: Array<{ quantity: string; days: string }>;
  company: { name: string; years: number | null; country: string } | null;
  customization: Array<{ name: string; detail: string }>;
  highlights: string[];
}

export interface TaskDetail {
  task: Task;
  content?: Record<string, unknown>;
  analysis?: Record<string, unknown>;
  retries: Array<Record<string, unknown>>;
  workflow?: Array<Record<string, unknown>>;
  alibaba_attributes?: Array<{ key: string; value: string }>;
  alibaba_modules?: AlibabaModules;
}

export interface KeywordStats {
  keyword: string;
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

export interface TaskFilters {
  limit?: number;
  offset?: number;
  status?: string;
  project_id?: string;
  archived?: boolean;
  domain?: string;
}

function buildQuery(filters: TaskFilters): string {
  const params = new URLSearchParams();
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  if (filters.status) params.set('status', filters.status);
  if (filters.project_id) params.set('project_id', filters.project_id);
  if (filters.archived) params.set('archived', 'true');
  if (filters.domain) params.set('domain', filters.domain);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const tasksApi = {
  list: (filters: TaskFilters = {}) =>
    api.get<Task[]>(`/tasks${buildQuery(filters)}`),

  stats: () =>
    api.get<TaskStats>('/tasks/stats'),

  get: (id: string) =>
    api.get<TaskDetail>(`/tasks/${id}`),

  create: (data: { url: string; keyword?: string; project_id?: string; priority?: number; webhook_url?: string; tag?: string; selected_stages?: string[] }) =>
    api.post<Task>('/tasks', data),

  createBatch: (data: { urls: string[]; keyword?: string; project_id?: string; priority?: number; tag?: string; selected_stages?: string[] }) =>
    api.post('/tasks/batch', data),

  retry: (id: string) =>
    api.post(`/tasks/${id}/retry`),

  batchRetry: (task_ids: string[]) =>
    api.post('/tasks/batch-retry', { task_ids }),

  smartRetryFailed: () =>
    api.post<{ total: number; retried: number; downgraded: number }>('/tasks/smart-retry-failed'),

  archive: (id: string) =>
    api.patch(`/tasks/${id}/archive`),

  batchUpdate: (task_ids: string[], action: 'archive' | 'unarchive') =>
    api.patch('/tasks/batch-update', { task_ids, action }),

  history: (url: string) =>
    api.get<Task[]>(`/tasks/history?url=${encodeURIComponent(url)}`),

  retries: (id: string) =>
    api.get(`/tasks/${id}/retries`),

  workflow: (id: string) =>
    api.get(`/tasks/${id}/workflow`),

  exportJson: (id: string) =>
    api.get(`/tasks/${id}/export?format=json`),

  getHtml: (id: string, source: 'qs' | 'ba' | 'iframe') =>
    api.get<{ source: string; size: number; html: string }>(`/tasks/${id}/html?source=${source}`),

  rescrapeProject: (project_id: string) =>
    api.post<{ total: number; queued: number }>('/tasks/rescrape-project', { project_id }),

  domainStats: () =>
    api.get<DomainStats[]>('/tasks/domain-stats'),

  unpauseDomain: (domain: string) =>
    api.delete<{ domain: string; unblocked: boolean }>(`/tasks/domain-pause/${encodeURIComponent(domain)}`),

  clearAllDomainPauses: () =>
    api.delete<{ cleared: number }>('/tasks/domain-pause'),

  browseractRetry: (id: string, language?: string) =>
    api.post<{ baTaskId: string; status: string }>(`/tasks/${id}/browseract-retry`, { language }),

  keywordStats: () =>
    api.get<KeywordStats[]>('/tasks/keyword-stats'),

  deleteByKeyword: (keyword: string) =>
    api.delete<{ keyword: string; deleted: number }>(`/tasks/by-keyword/${encodeURIComponent(keyword)}`),

  pauseByKeyword: (keyword: string) =>
    api.patch<{ keyword: string; paused: number }>(`/tasks/by-keyword/${encodeURIComponent(keyword)}/pause`),

  resetStuck: (maxAgeMinutes?: number) =>
    api.post<{ reset: number }>('/tasks/reset-stuck', { max_age_minutes: maxAgeMinutes ?? 60 }),
};
